import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import { LinkButton, LoadingSpinner } from '@onefootprint/ui';
import { Trans, useTranslation } from 'react-i18next';
import FieldOrPlaceholder from '@/components/field-or-placeholder';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import getDecryptableDIs from '@/utils/get-decryptable-dis';
import useAddressFieldsProps from '../../hooks/use-address-fields-props';
import CardBase from '../card-base';
import CardRow from '../card-row';
import AddressCardIcon from './components/address-card-icon';
import type AddressType from './types';
type AddressCardProps = {
  id: string;
  type: AddressType;
  entity: Entity;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  isPending?: boolean;
};
const AddressCard = t0 => {
  const $ = _c(45);
  const {
    id,
    type,
    entity,
    isSelected,
    onSelect,
    isPending
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "deviceInsights.addressCard"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  let t2;
  if ($[1] !== entity.data || $[2] !== entity.id) {
    t2 = {
      entityAttributes: entity.data,
      entityId: entity.id
    };
    $[1] = entity.data;
    $[2] = entity.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const entityVaultQuery = useEntityVault(t2);
  const {
    getAddressFieldsProps,
    getAddressDis
  } = useAddressFieldsProps(entity);
  let t3;
  if ($[4] !== t || $[5] !== type) {
    t3 = type === "business" ? t(_temp) : t(_temp2);
    $[4] = t;
    $[5] = type;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const headerText = t3;
  let t4;
  if ($[7] !== entity) {
    const decryptableDIs = getDecryptableDIs(entity);
    t4 = new Set(decryptableDIs);
    $[7] = entity;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const decryptableSet = t4;
  let t5;
  if ($[9] !== decryptableSet || $[10] !== getAddressFieldsProps || $[11] !== type) {
    const encryptedFields = getAddressFieldsProps(type).filter(_temp3);
    let t6;
    if ($[13] !== decryptableSet) {
      t6 = field => !field || decryptableSet.has(field.name) && field.canDecrypt;
      $[13] = decryptableSet;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = encryptedFields.filter(t6);
    $[9] = decryptableSet;
    $[10] = getAddressFieldsProps;
    $[11] = type;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const decryptableFields = t5;
  const decryptControls = useDecryptControls();
  const isCtaLoading = isPending || decryptControls.isDecrypting;
  const isCtaVisible = decryptableFields.length > 0 || isCtaLoading;
  let t6;
  if ($[15] !== decryptControls || $[16] !== entity || $[17] !== entityVaultQuery || $[18] !== getAddressDis || $[19] !== t || $[20] !== type) {
    t6 = async () => {
      const dis = getAddressDis(type);
      const response = await decryptControls.decrypt({
        entity,
        fields: dis,
        reason: t(_temp4)
      });
      entityVaultQuery.update({
        vault: response,
        transforms: {},
        dataKinds: {}
      });
    };
    $[15] = decryptControls;
    $[16] = entity;
    $[17] = entityVaultQuery;
    $[18] = getAddressDis;
    $[19] = t;
    $[20] = type;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleClick = t6;
  let t7;
  if ($[22] !== handleClick || $[23] !== isCtaLoading || $[24] !== isCtaVisible || $[25] !== t) {
    t7 = () => {
      if (!isCtaVisible) {
        return null;
      }
      return isCtaLoading ? <LoadingSpinner color="accent" /> : <div className="flex flex-col w-full items-start"><div className="text-label-2 text-primary md:flex md:gap-1"><Trans ns="entity-details" i18nKey={_temp5} components={{
            link: <LinkButton onClick={handleClick}>{t(_temp6)}</LinkButton>
          }} /></div></div>;
    };
    $[22] = handleClick;
    $[23] = isCtaLoading;
    $[24] = isCtaVisible;
    $[25] = t;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const renderCta = t7;
  let t8;
  if ($[27] !== renderCta) {
    t8 = renderCta();
    $[27] = renderCta;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== type) {
    t9 = <AddressCardIcon type={type} size="large" />;
    $[29] = type;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== id || $[32] !== onSelect) {
    t10 = () => onSelect?.(id);
    $[31] = id;
    $[32] = onSelect;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== getAddressFieldsProps || $[35] !== type) {
    t11 = getAddressFieldsProps(type).map(_temp7);
    $[34] = getAddressFieldsProps;
    $[35] = type;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== headerText || $[38] !== id || $[39] !== isSelected || $[40] !== t10 || $[41] !== t11 || $[42] !== t8 || $[43] !== t9) {
    t12 = <CardBase cta={t8} headerIcon={t9} headerText={headerText} id={id} isSelected={isSelected} onSelect={t10} rows={t11} />;
    $[37] = headerText;
    $[38] = id;
    $[39] = isSelected;
    $[40] = t10;
    $[41] = t11;
    $[42] = t8;
    $[43] = t9;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  return t12;
};
export default AddressCard;
function _temp($) {
  return $.business.headerText;
}
function _temp2($_0) {
  return $_0.residential.headerText;
}
function _temp3(prop) {
  return !prop.isDecrypted;
}
function _temp4($_1) {
  return $_1.decryptReason;
}
function _temp5($_2) {
  return $_2.deviceInsights.addressCard.cta.text;
}
function _temp6($_3) {
  return $_3.cta.link;
}
function _temp7(prop_0) {
  return <CardRow key={prop_0.label} label={prop_0.label} value={<FieldOrPlaceholder data={prop_0.value} transforms={prop_0.transforms} />} />;
}